import React from 'react';
import styled from 'styled-components';

const DetailContainer = styled.div`
  padding: 2rem;
  background: #f3f5f7;
  min-height: 100vh;
`;

const Header = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: #6b7280;
`;

const OverallSection = styled.div`
  background: #f3f5f7;
  padding: 2rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ScoreSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ScoreCircle = styled.div`
  position: relative;
  width: 200px;
  height: 200px;
  margin-bottom: 1rem;
`;

const ScoreText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
`;

const Score = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: ${props => {
    if (props.score >= 70) return '#EF4444';
    if (props.score >= 40) return '#F59E0B';
    return '#10B981';
  }};
`;

const ScoreLabel = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0.25rem;
`;

const RiskBadge = styled.div`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  background: ${props => {
    if (props.risk === 'High') return '#FEE2E2';
    if (props.risk === 'Medium') return '#FEF3C7';
    return '#D1FAE5';
  }};
  color: ${props => {
    if (props.risk === 'High') return '#DC2626';
    if (props.risk === 'Medium') return '#D97706';
    return '#059669';
  }};
`;

const SummarySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SummaryTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
`;

const SummaryText = styled.p`
  font-size: 0.95rem;
  color: #4b5563;
  line-height: 1.6;
`;

const ResultBadge = styled.div`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  background: #DBEAFE;
  color: #1E40AF;
`;

const DimensionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
`;

const DimensionCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border-left: 4px solid ${props => {
    if (props.result === 'Failed' || props.result === 'Investigate') return '#EF4444';
    return '#10B981';
  }};
`;

const DimensionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const DimensionName = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
`;

const DimensionScore = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => {
    if (props.score >= 20) return '#EF4444';
    if (props.score > 0) return '#F59E0B';
    return '#10B981';
  }};
`;

const DimensionResult = styled.div`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-top: 0.5rem;
  background: ${props => {
    if (props.result === 'Failed' || props.result === 'Investigate') return '#FEE2E2';
    return '#D1FAE5';
  }};
  color: ${props => {
    if (props.result === 'Failed' || props.result === 'Investigate') return '#DC2626';
    return '#059669';
  }};
`;

const RulesSection = styled.div`
  margin-top: 1rem;
`;

const RuleItem = styled.div`
  padding: 1rem;
  background: #f3f5f7;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  border-left: 3px solid ${props => {
    if (props.result === 'Failed') return '#EF4444';
    return '#10B981';
  }};
`;

const RuleName = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
`;

const RuleDetails = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
`;

const RuleReason = styled.div`
  font-size: 0.875rem;
  color: #4b5563;
  font-style: italic;
  margin-top: 0.5rem;
`;

const RuleScore = styled.span`
  float: right;
  font-weight: 700;
  color: ${props => {
    if (props.score >= 20) return '#EF4444';
    if (props.score > 0) return '#F59E0B';
    return '#10B981';
  }};
`;

const FWARiskDetailNew = ({ card }) => {
  const fwaData = card?.fwaData;
  const fwaResponse = fwaData?.fwaResponse;

  if (!fwaData || !fwaResponse) {
    return (
      <DetailContainer>
        <Header>
          <Title>FWA Risk Assessment</Title>
          <Subtitle>No data available</Subtitle>
        </Header>
      </DetailContainer>
    );
  }

  const overallScore = parseInt(fwaResponse.overall_score) || 0;
  const overallRisk = fwaResponse.overall_risk || 'Low';
  const overallResult = fwaResponse.overall_result || 'Passed';
  const caseSummary = fwaResponse.case_summary || '';
  const caseAnalysis = fwaResponse.case_analysis || '';
  const displayName = fwaResponse.displayName || 'FWA Risk Assessment';
  const pipelineName = fwaData.pipelineData?.name || 'Fraud, Waste, and Abuse Analysis';
  
  // Use dimensions directly from API response
  const dimensions = fwaResponse.dimensions || [];

  const getCircleProgress = (score) => {
    const radius = 90;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (score / 100) * circumference;
    return { circumference, offset };
  };

  const { circumference, offset } = getCircleProgress(overallScore);

  return (
    <DetailContainer>
      <Header>
        <Title>{displayName}</Title>
        <Subtitle>{pipelineName}</Subtitle>
      </Header>

      <OverallSection>
        <ScoreSection>
          <ScoreCircle>
            <svg width="200" height="200" viewBox="0 0 200 200">
              <circle
                cx="100"
                cy="100"
                r="90"
                fill="none"
                stroke="#E5E7EB"
                strokeWidth="12"
              />
              <circle
                cx="100"
                cy="100"
                r="90"
                fill="none"
                stroke={overallScore >= 70 ? '#EF4444' : overallScore >= 40 ? '#F59E0B' : '#10B981'}
                strokeWidth="12"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                strokeLinecap="round"
                transform="rotate(-90 100 100)"
                style={{ transition: 'stroke-dashoffset 1s ease' }}
              />
            </svg>
            <ScoreText>
              <Score score={overallScore}>{overallScore}</Score>
              <ScoreLabel>Risk Score</ScoreLabel>
            </ScoreText>
          </ScoreCircle>
          <RiskBadge risk={overallRisk}>Risk Level: {overallRisk}</RiskBadge>
        </ScoreSection>

        <SummarySection>
          <div>
            <SummaryTitle>Overall Result</SummaryTitle>
            <ResultBadge>{overallResult}</ResultBadge>
          </div>
          
          {caseSummary && (
            <div>
              <SummaryTitle>Case Summary</SummaryTitle>
              <SummaryText>{caseSummary}</SummaryText>
            </div>
          )}
          
          {caseAnalysis && (
            <div>
              <SummaryTitle>Case Analysis</SummaryTitle>
              <SummaryText>{caseAnalysis}</SummaryText>
            </div>
          )}
        </SummarySection>
      </OverallSection>

      <DimensionsGrid>
        {dimensions.map((dimension, index) => (
          <DimensionCard key={index} result={dimension.overall_result}>
            <DimensionHeader>
              <div>
                <DimensionName>{dimension.name}</DimensionName>
                <DimensionResult result={dimension.overall_result}>
                  {dimension.overall_result}
                </DimensionResult>
              </div>
              <DimensionScore score={dimension.overall_score}>
                {dimension.overall_score}
              </DimensionScore>
            </DimensionHeader>

            <RulesSection>
              {dimension.rules && dimension.rules.map((rule, ruleIndex) => (
                <RuleItem key={ruleIndex} result={rule.result}>
                  <RuleName>
                    {rule.name}
                    <RuleScore score={rule.score}>{rule.score} pts</RuleScore>
                  </RuleName>
                  <RuleDetails>
                    <strong>Type:</strong> {rule.type} - {rule.sub_type}
                  </RuleDetails>
                  <RuleDetails>
                    <strong>Rule:</strong> {rule.rule}
                  </RuleDetails>
                  <RuleReason>
                    <strong>Reason:</strong> {rule.reason}
                  </RuleReason>
                </RuleItem>
              ))}
            </RulesSection>
          </DimensionCard>
        ))}
      </DimensionsGrid>
    </DetailContainer>
  );
};

export default FWARiskDetailNew;
